import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { ACTION_SEPARATOR, MoreActionMenuItemType } from "models/propTypes/menuPropTypes";
import { CommonConstants, IconDefinitionConstants } from "utils/constants";
import { ProfileButtonsProps } from "models/propTypes/timeReportingProjectPropTypes";
import { WintSuperkollAPIModelTimeReporting } from "wint-api-client/dist/types";
import { DialogEnums, StyleEnums } from "models/enums";
import { TimeReportingFormTypes } from "models/formTypes";
//#endregion
//#region components
import { DashboardButtonGroup, FilterButton, useNavigationHistory } from "components/common";
import { MoreActionsButton, useFilterContext } from "components/common";
import { useNavigate } from "react-router-dom";
import { useTimeReportingService, useTimeReportingProjectService } from "serviceHooks";
//#endregion
import { ExportHelper, LocalizationHelperInstance, UrlHelper } from "utils/helpers";

const CreateProfileButtons: React.FC<ProfileButtonsProps> = (props) => {
  const timeReportingService = useTimeReportingService();
  const timeReportingProjectService = useTimeReportingProjectService();

  const {
    project,
    onCreateInvoiceDrafts,
    projectHasUnInvoicedProjectTime,
    createInvoiceDraftsButtonEnabled,
    createInvoiceDraftsButtonSubmitting,
    connectInvoiceDialog,
  } = props;
  const { filter } = useFilterContext<TimeReportingFormTypes.Filter>();

  const navigate = useNavigate();
  const [hideInactiveProjectAction, setHideInactiveProjectAction] = React.useState<boolean>();
  const { goBack } = useNavigationHistory();

  const inactivateProject = () => {
    return timeReportingProjectService.inactivateTimeReportingProject(project.id as string);
  };

  const deleteProject = () => {
    return timeReportingProjectService.deleteTimeReportingProject(project.id as string);
  };

  const goToSettingsAction: MoreActionMenuItemType = {
    description: `${LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.TimeReportingProject.EditTimeReportingProject
    )}`,
    hidden: timeReportingProjectService.hasReadOnlyPermissions(),
    icon: IconDefinitionConstants.WSettings,
    onClickHandler: async () => {
      navigate(UrlHelper.formatPath(`/Projects/${project.id}/Settings`));
    },
  };

  const inactivateProjectAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReportingProject.Inactivate),
    icon: IconDefinitionConstants.WStop,
    onClickHandler: inactivateProject,
    successMessage: LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.TimeReportingProject.ProjectInactivated
    ),
    hidden: hideInactiveProjectAction || timeReportingProjectService.hasReadOnlyPermissions(),
    onActionSuccess: () => setHideInactiveProjectAction(true),
  };

  const deleteProjectAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReportingProject.Delete),
    hidden: timeReportingProjectService.hasReadOnlyPermissions(),
    icon: IconDefinitionConstants.WTrashAlt,
    actionDialog: {
      isOpen: false,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReportingProject.Delete),
      titleIcon: IconDefinitionConstants.WTimes,
      dialogType: DialogEnums.DialogType.Message,
      actions: [
        {
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Remove),
          onClick: deleteProject,
          type: StyleEnums.DisplayType.Danger,
          successMessage: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.TimeReportingProject.ProjectDeleted
          ),
        },
      ],
      children: projectHasUnInvoicedProjectTime
        ? LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.TimeReportingProject.WarningMessageUnInvoicedProjectTimeProjectDeletion
          )
        : LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.TimeReportingProject.WarningMessageProjectDeletion
          ),
      type: StyleEnums.DisplayType.Danger,
    },
    highlightType: StyleEnums.DisplayType.Danger,
    onActionSuccess: () =>
      setTimeout(() => {
        goBack();
      }, 100),
  };

  const onExportToExcel = async () => {
    await timeReportingService
      .exportReport(
        {
          ...filter,
        },
        project.id
      )
      .then((res) => {
        const arrayWithHeaders = createHeadline();
        res.forEach((r) => {
          arrayWithHeaders.push(mapToColumns(r));
        });

        exportToExcel(arrayWithHeaders);
      });
  };

  function mapToColumns(r: WintSuperkollAPIModelTimeReporting.TimeReportingExportRow) {
    return [
      r.person,
      LocalizationHelperInstance.getFormattedDate(r.date) ?? "",
      r.project,
      r.category,
      r.hours,
      r.rate,
      r.externalComment,
      r.internalComment,
    ];
  }

  function createHeadline() {
    const headline: any[][] = [];

    headline.push([
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.ExportPersons),
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.ExportDate),
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.ExportProject),
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.ExportCategory),
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.ExportHours),
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.ExportRate),
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.ExportExternalComment),
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.ExportInternalComment),
    ]);

    return headline;
  }

  function exportToExcel(rows: any[][]) {
    const title = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReportingProject.Details);
    const filename = `${project.name} -  ${title}.${CommonConstants.ExcelFileExtension}`;

    ExportHelper.exportReport(rows, filename, title);

    return Promise.resolve(false);
  }

  const exportToExcelAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.TimeReportingProject.ExportDetailedReport
    ),
    icon: IconDefinitionConstants.WSave,
    onClickHandler: onExportToExcel,

    successMessage: LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.TimeReportingProject.TimeReportsExported
    ),

    hidden: hideInactiveProjectAction,
  };

  const getMenuItems = (): MoreActionMenuItemType[] => {
    const actions: MoreActionMenuItemType[] = [];
    actions.push(goToSettingsAction);

    if (project?.projectActiveState === WintSuperkollAPIModelTimeReporting.ProjectActiveState.Active) {
      actions.push(inactivateProjectAction);
    }

    if (project?.projectActiveState !== WintSuperkollAPIModelTimeReporting.ProjectActiveState.Deleted) {
      actions.push(deleteProjectAction);
    }

    if (project?.projectActiveState !== WintSuperkollAPIModelTimeReporting.ProjectActiveState.Deleted) {
      actions.push(exportToExcelAction);
    }

    return actions;
  };

  return (
    <DashboardButtonGroup
      buttons={[
        <MoreActionsButton
          size="sm"
          menuItems={[
            {
              description: LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.TimeReportingProject.MarkAsInvoiced
              ),
              actionDialog: connectInvoiceDialog,
              icon: IconDefinitionConstants.WMoney,
            },
          ]}
          onClick={onCreateInvoiceDrafts}
          menuPlacement="bottom"
          disabled={
            !(
              createInvoiceDraftsButtonEnabled &&
              project?.projectActiveState !== WintSuperkollAPIModelTimeReporting.ProjectActiveState.Deleted &&
              !timeReportingProjectService.hasReadOnlyPermissions()
            )
          }
          isSubmitting={createInvoiceDraftsButtonSubmitting}
          type="submit"
        >
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReportingProject.InvoiceTheRemaining)}
        </MoreActionsButton>,
        ACTION_SEPARATOR,
        <FilterButton />,
      ]}
      moreActions={getMenuItems()}
    ></DashboardButtonGroup>
  );
};

export default CreateProfileButtons;
